import type { ImageWidget } from "~/types/widgets"
import type { Product } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"
import ProductCard from "~/components/product/ProductCard"
import Icon from "~/components/ui/Icon"
import Slider from "~/components/ui/Slider"
import SliderJS from "~/components/ui/SliderJS"
import clx from "~/sdk/clx"
import trimProductForShelf from "~/sdk/trimProductForShelf"
import { useId, useState } from "react"
import clsx from "~/sdk/clx"

/** @titleBy title */
interface Tab {
  /** @title Título da Aba (tab) */
  title: string

  /**
   * @title Imagem da Aba (tab)
   * @description Preferencialmente usar formato SVG
   */
  image: ImageWidget

  /**
   * @title Imagem da Aba (tab) - hover do mouse
   * @description Preferencialmente usar formato SVG
   */
  imageHover: ImageWidget

  /** @title Produtos da Aba (tab) */
  products: Product[] | null
}

export interface Props {
  /**
   * @title Título
   */
  title?: string

  /**
   * @title Abas (tabs)
   */
  tabs: Tab[]

  /**
   * @title Tab index
   * @description Se não for definido um tab index, a exibição começará pela primeira aba. Se for definido um número diferente de 0, a exibição será iniciada na aba correspondente a esse número. Por exemplo, colocar 1 fará com que a exibição comece pela segunda aba (a primeira aba possui index 0)
   */
  tabIndex?: number

  /**
   *  @title Defer Mode
   *  @description Defer atrasa o carregamento da seção até que o usuário role a página (scroll) ou a seção entre na tela (intersection).
   */
  defer?: 'scroll' | 'intersection' | null

  /**
   * @title Configurações da prateleira
   */
  layout?: {
    /**
     * @title Número de slides (produtos) por visualização
     */
    numberOfSlides?: {
      mobile?: 1 | 2 | 3 | 4 | 5
      tablet?: 1 | 2 | 3 | 4 | 5
      desktop?: 1 | 2 | 3 | 4 | 5
    }

    /**
     * @title Mostrar as setas
     * @default true
     */
    showArrows?: boolean
  }
}

export function loader(props: Props & { isMobile?: boolean }) {
  return {
    ...props,
    // Every tab ships a full product list into the payload — trim to what the
    // card renders (see ~/sdk/trimProductForShelf).
    tabs: props.tabs?.map((tab) => ({
      ...tab,
      products: tab.products?.map(trimProductForShelf) ?? tab.products,
    })),
    isMobile: props.isMobile ?? false,
  }
}

const slideDesktop = {
  1: 'xl:w-full',
  2: 'xl:w-[calc((100%-(16px))/2)]',
  3: 'xl:w-[calc((100%-(16px*2))/3)]',
  4: 'xl:w-[calc((100%-(16px*3))/4)]',
  5: 'xl:w-[calc((100%-(16px*4))/5)]',
}

const slideMobile = {
  1: 'w-full',
  2: 'w-[calc((100%-(16px))/2)]',
  3: 'w-[calc((100%-(16px*2))/3)]',
  4: 'w-[calc((100%-(16px*3))/4)]',
  5: 'w-[calc((100%-(16px*4))/5)]',
}

const slideTablet = {
  1: 'sm:w-full',
  2: 'sm:w-[calc((100%-(16px))/2)]',
  3: 'sm:w-[calc((100%-(16px*2))/3)]',
  4: 'sm:w-[calc((100%-(16px*3))/4)]',
  5: 'sm:w-[calc((100%-(16px*4))/5)]',
}

function BrandTabbedProductShelf(
  { title, tabs, tabIndex, isMobile, layout }: ReturnType<typeof loader>,
) {
  const id = useId()
  const initialTab = typeof tabIndex === 'number'
    ? Math.min(Math.max(tabIndex, 0), Math.max(tabs.length - 1, 0))
    : 0
  const [ti, setTi] = useState(initialTab)
  const { products } = tabs[ti] ?? { products: null }

  if (!products || products?.length === 0) return null

  const { showArrows = true } = layout ?? {}
  const hasOnePage = products.length <= 5

  return (
    <div className='w-full max-w-[1834px] mx-auto px-4 mt-10 lg:mt-12 flex flex-col lg:flex-row gap-6 lg:gap-10 2xl:gap-32 items-center'>
      <div className='flex flex-col gap-7 lg:gap-10 flex-1 w-full lg:w-auto'>
        {!!title && (
          <span className='text-3xl text-[#3F3F3F] font-bold lg:max-w-96 lg:min-w-96'>
            {title}
          </span>
        )}

        <div className='grid grid-cols-2 gap-2 2xl:gap-5'>
          {tabs?.map((tab, index) => (
            <button
              key={tab.title ?? index}
              type='button'
              className='relative w-full lg:max-w-[392px] aspect-[392/167] flex items-center justify-center overflow-hidden group'
              aria-selected={index === ti}
              aria-label={tab.title}
              title={tab.title}
              onClick={() => setTi(index)}
            >
              <Image
                src={tab.image}
                className={`absolute w-full h-full object-cover duration-300 group-hover:opacity-0 ${
                  index === ti ? 'opacity-0' : ''
                }`}
                width={390}
                height={165}
                alt={tab.title}
              />
              <Image
                src={tab.imageHover}
                className={`absolute w-full h-full object-cover duration-300 opacity-0 group-hover:opacity-100 ${
                  index === ti ? 'opacity-100' : ''
                }`}
                width={390}
                height={165}
                alt={tab.title}
              />
            </button>
          ))}
        </div>
      </div>

      <div id={id} data-has-one-page={hasOnePage} className='max-w-full lg:max-w-[53%] 2xl:max-w-[50%]'>
        <div className='flex flex-row items-center relative lg:px-14 2xl:px-[72px]'>
          {showArrows && (
            <div
              className={clsx(
                'z-[1] absolute top-[calc((100%-254px)/2)] lg:top-1/2 -left-2 lg:left-0 -translate-y-1/2',
                products.length <= 2 && 'hidden',
              )}
            >
              <Slider.PrevButton className='btn btn-circle bg-opacity-80 text-salmon active:text-black active:bg-[#e1dfdb] active:bg-opacity-80 lg:hover:text-black bg-[#e1dfdb] lg:hover:bg-[#e1dfdb] lg:hover:bg-opacity-80 border-none'>
                <Icon size={24} id='ChevronLeft' strokeWidth={3} />
              </Slider.PrevButton>
            </div>
          )}

          <Slider className='carousel sm:carousel-end flex-1 gap-4'>
            {products?.map((product, index) => (
              <Slider.Item
                index={index}
                className={clx(
                  'carousel-item',
                  slideDesktop[layout?.numberOfSlides?.desktop ?? 2],
                  slideTablet[layout?.numberOfSlides?.tablet ?? 2],
                  slideMobile[layout?.numberOfSlides?.mobile ?? 2],
                )}
              >
                <ProductCard
                  product={product}
                  preload={index === 0}
                  isMobile={isMobile as boolean}
                  index={index}
                />
              </Slider.Item>
            ))}
          </Slider>

          {showArrows && (
            <div
              className={clsx(
                'z-[1] absolute top-[calc((100%-254px)/2)] lg:top-1/2 -right-2 lg:right-0 -translate-y-1/2',
                products.length <= 2 && 'hidden',
              )}
            >
              <Slider.NextButton className='btn btn-circle bg-opacity-80 text-salmon active:text-black active:bg-[#e1dfdb] active:bg-opacity-80 lg:hover:text-black bg-[#e1dfdb] lg:hover:bg-[#e1dfdb] lg:hover:bg-opacity-80 border-none'>
                <Icon size={24} id='ChevronRight' strokeWidth={3} />
              </Slider.NextButton>
            </div>
          )}
        </div>
        <SliderJS rootId={id} infinite />
      </div>
    </div>
  )
}

export default BrandTabbedProductShelf
